import { Database, FileUp, Upload, SquareCheckBig } from "lucide-react";
import { useCallback, useState } from "react";

import { directories, getDirectoryLabel } from "./model";

import {
  useUploadEtsng,
  useUploadRailcoaches,
  useUploadRailroads,
  useUploadRailstations,
} from "@/common/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/common/components";
import { useToast } from "@/common/hooks";

export function DirectoriesPage() {
  const [uploadingDirectory, setUploadingDirectory] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const { mutateAsync: uploadRailroadsMutateAsync } = useUploadRailroads();
  const { mutateAsync: uploadRailstationsMutateAsync } = useUploadRailstations();
  const { mutateAsync: uploadEtsngMutateAsync } = useUploadEtsng();
  const { mutateAsync: uploadRailcoachesMutateAsync } = useUploadRailcoaches();

  const handleFileUpload = useCallback(
    (directoryType: string, event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      const mutationMap: Record<string, (variables: { data: { file: File } }) => Promise<unknown>> =
        {
          railways: uploadRailroadsMutateAsync,
          stations: uploadRailstationsMutateAsync,
          etsngs: uploadEtsngMutateAsync,
          wagonModels: uploadRailcoachesMutateAsync,
        };

      const mutation = mutationMap[directoryType];

      if (!mutation) {
        return;
      }

      setUploadingDirectory(directoryType);

      mutation({ data: { file } })
        .then(() => {
          setUploadedFiles((prev) => ({ ...prev, [directoryType]: file.name }));
          toast({
            title: "Успешно",
            description: `Справочник "${getDirectoryLabel(directoryType)}" успешно загружен`,
          });
        })
        .catch((error: unknown) => {
          toast({
            title: "Ошибка",
            description: error instanceof Error ? error.message : "Не удалось загрузить файл",
            variant: "destructive",
          });
        })
        .finally(() => {
          setUploadingDirectory(null);
        });
    },
    [
      toast,
      uploadRailroadsMutateAsync,
      uploadRailstationsMutateAsync,
      uploadEtsngMutateAsync,
      uploadRailcoachesMutateAsync,
    ],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Database className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Справочники</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Управление справочниками
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="text-sm text-muted-foreground mb-4">
              <p>Здесь вы можете загрузить справочники в формате Excel.</p>
              <p>После загрузки данные справочников будут доступны в системе.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {directories.map((directory) => (
                <Card
                  key={directory.type}
                  className="border-2 border-dashed border-gray-200 hover:border-primary transition-colors"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FileUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{directory.label}</h3>
                        <p className="text-sm text-muted-foreground">{directory.description}</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground mb-2">
                        Поддерживаемые форматы: {directory.acceptedFormats}
                      </p>
                    </div>

                    <div className="relative">
                      <input
                        id={`file-${directory.type}`}
                        type="file"
                        accept={directory.acceptedFormats}
                        onChange={(e) => handleFileUpload(directory.type, e)}
                        disabled={uploadingDirectory === directory.type}
                        className="hidden"
                      />

                      <label
                        htmlFor={`file-${directory.type}`}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-dashed border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                      >
                        {uploadedFiles[directory.type] || "Выберите файл справочника"}
                      </label>

                      {uploadingDirectory === directory.type && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md">
                          <div className="flex items-center gap-2 text-sm text-primary">
                            <Upload className="h-4 w-4 animate-pulse" />
                            Загрузка...
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div
                        className={`flex items-center gap-2 text-sm ${
                          uploadedFiles[directory.type] ? "text-green-600" : "text-orange-500"
                        }`}
                      >
                        {uploadedFiles[directory.type] && <SquareCheckBig className="h-5 w-5" />}
                        {uploadedFiles[directory.type]
                          ? "Справочник обновлен"
                          : "Загрузите актуальный справочник"}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
